import { useLayoutEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap, ScrollTrigger } from '../lib/gsapConfig';

const steps = [
  {
    number: '01',
    title: 'Enter your moving details',
    description: 'Tell us where you are moving, what you need moved, and when you want help.',
    icon: 'M8 7h8M8 11h8M8 15h5M6 3h12a2 2 0 012 2v14l-4-2-4 2-4-2-4 2V5a2 2 0 012-2z',
  },
  {
    number: '02',
    title: 'Compare trusted movers',
    description: 'Review matched moving partners, services, availability, and quote details.',
    icon: 'M4 7h16M7 7v12m10-12v12M5 19h18M9 11h2m4 0h2M9 15h2m4 0h2',
  },
  {
    number: '03',
    title: 'Choose with confidence',
    description: 'Pick the mover that fits your route, budget, and timing before move day.',
    icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
  },
];

const HowItWorks = () => {
  const sectionRef = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const words = gsap.utils.toArray('.hiw-word');
      const cards = gsap.utils.toArray('.step-card');

      const reveal = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 78%',
          once: true,
          invalidateOnRefresh: true,
        },
      });

      reveal
        .fromTo(words, {
          y: 48,
          autoAlpha: 0,
        }, {
          y: 0,
          autoAlpha: 1,
          duration: 0.75,
          stagger: 0.06,
          ease: 'power4.out',
          immediateRender: true,
          clearProps: 'transform',
        })
        .fromTo(cards, {
          y: 34,
          autoAlpha: 0,
        }, {
          y: 0,
          autoAlpha: 1,
          duration: 0.6,
          stagger: 0.1,
          ease: 'power3.out',
          immediateRender: true,
          clearProps: 'transform',
        }, 0.16);
    }, sectionRef);

    const refreshTimer = window.setTimeout(() => ScrollTrigger.refresh(), 150);

    return () => {
      window.clearTimeout(refreshTimer);
      ctx.revert();
    };
  }, []);

  return (
    <section id="how-it-works" ref={sectionRef} className="bg-white py-14 sm:py-16">
      <div className="container mx-auto px-4">
        <div className="mx-auto mb-10 max-w-3xl text-center">
          <span className="inline-flex rounded-full bg-primary/10 px-4 py-2 text-sm font-black text-primary">
            How it works
          </span>
          <h2 className="mt-4 flex flex-wrap justify-center gap-x-2 gap-y-1 text-3xl font-black leading-tight tracking-normal text-slate-950 sm:text-5xl">
            {['Plan', 'your', 'move', 'in', 'three', 'steps!'].map((word) => (
              <span key={word} className="inline-block overflow-hidden pb-1">
                <span className="hiw-word inline-block" style={{ opacity: 0, visibility: 'hidden', transform: 'translateY(48px)' }}>{word}</span>
              </span>
            ))}
          </h2>
          <p className="mt-4 text-lg leading-8 text-slate-600">
            Start with one clear request, then compare movers before choosing who to contact.
          </p>
        </div>

        <div className="grid gap-5 lg:grid-cols-3">
          {steps.map((step) => (
            <div key={step.number} className="step-card relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_10px_30px_rgba(15,23,42,0.06)] transition duration-300 hover:-translate-y-1 hover:border-primary/35 hover:shadow-[0_16px_38px_rgba(235,152,19,0.12)]" style={{ opacity: 0, visibility: 'hidden', transform: 'translateY(34px)' }}>
              <div className="absolute right-5 top-5 text-6xl font-black leading-none text-slate-100">
                {step.number}
              </div>
              <div className="relative z-10">
                <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-950 text-primary">
                  <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={step.icon} />
                  </svg>
                </div>
                <h3 className="text-xl font-black text-slate-950">{step.title}</h3>
                <p className="mt-3 text-sm leading-6 text-slate-600">{step.description}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 text-center">
          <Link to="/quote" className="inline-flex min-h-[50px] items-center justify-center rounded-xl bg-primary px-6 text-sm font-black text-white shadow-[0_12px_26px_rgba(235,152,19,0.22)] transition hover:bg-primary-dark">
            Start your move
            <svg className="ml-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.4} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
